import { Injectable } from '@angular/core'
import { GridApi } from '@ag-grid-community/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'
import {
  DEFAULT_SOURCE,
  GridRole,
  gridKeyOf,
  MatchTargetKey,
  ROLE_ALLOWED,
  SourceKey,
} from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { MatchingService } from './matching.service'
import { WorkbenchDataSource } from './workbench-data-source'

type PerRole<T> = Record<GridRole, T>

const emptySelection = (): PerRole<Set<string>> => ({
  comparison: new Set<string>(),
  base: new Set<string>(),
})

const setsEqual = (a: Set<string>, b: Set<string>): boolean =>
  a.size === b.size && [...a].every(v => b.has(v))

/**
 * Panel-scoped mediator for the two grids. Owns source-per-role assignment, selection sync,
 * cross-filter targets and the shared row set. The grids talk only to this service, never to each
 * other. Provided at the host component so there is exactly one instance per panel.
 */
@Injectable()
export class WorkbenchStoreService {
  /**
   * Rows per source, published by whichever grid is showing that source once its catalog resolves
   * and its page comes back. A source no grid is pointed at has no rows loaded, so match counts
   * against it read zero until it is displayed.
   */
  private readonly rowsBySource = new Map<SourceKey, WorkbenchRow[]>()

  private readonly rowsSubject = new Subject<void>()

  private readonly sourceSubject = new BehaviorSubject<PerRole<SourceKey>>({
    comparison: DEFAULT_SOURCE.comparison,
    base: DEFAULT_SOURCE.base,
  })
  private readonly selectionSubject = new BehaviorSubject<PerRole<Set<string>>>(
    emptySelection(),
  )
  private readonly crossFilterSubject = new BehaviorSubject<
    PerRole<string[] | null>
  >({
    comparison: null,
    base: null,
  })

  private readonly accountIdSubject = new BehaviorSubject<number | null>(null)

  private readonly apis: Partial<PerRole<GridApi>> = {}

  /** The account loaded into the active session. The field catalog is fetched per
   * (accountId, source), so the grids react to the session moving to another account. */
  accountId$: Observable<number | null> = this.accountIdSubject.pipe(
    distinctUntilChanged(),
  )

  /** Fires whenever any source's rows are replaced, so both grids can re-read what they need. */
  readonly rowsChanged$: Observable<void> = this.rowsSubject.asObservable()

  constructor(private readonly matching: MatchingService) {}

  // ---- source assignment ----------------------------------------------------

  sourceValue(role: GridRole): SourceKey {
    return this.sourceSubject.value[role]
  }

  sourceFor(role: GridRole): Observable<SourceKey> {
    return this.sourceSubject.pipe(
      map(sources => sources[role]),
      distinctUntilChanged(),
    )
  }

  /**
   * Point a grid at a source, honouring the allowed roles and the Enfusion mutual-exclusion rule
   * (Enfusion may only occupy one grid; selecting it in both auto-swaps the other back to its
   * default). Selection + cross-filters reset on any change.
   */
  setSource(role: GridRole, key: SourceKey): void {
    if (!ROLE_ALLOWED[role].includes(key)) return
    const current = this.sourceSubject.value
    if (current[role] === key) return
    const next: PerRole<SourceKey> = { ...current, [role]: key }
    if (key === 'api') {
      const other = this.otherRole(role)
      if (next[other] === 'api') {
        next[other] = other === 'comparison' ? 'prom' : 'cw'
      }
    }
    this.sourceSubject.next(next)
    this.selectionSubject.next(emptySelection())
    this.crossFilterSubject.next({ comparison: null, base: null })
  }

  private otherRole(role: GridRole): GridRole {
    return role === 'comparison' ? 'base' : 'comparison'
  }

  // ---- account --------------------------------------------------------------

  accountIdValue(): number | null {
    return this.accountIdSubject.value
  }

  setAccountId(accountId: number | null): void {
    if (this.accountIdSubject.value === accountId) return
    this.accountIdSubject.next(accountId)
  }

  // ---- rows -----------------------------------------------------------------

  rowsForSource(key: SourceKey): WorkbenchRow[] {
    return this.rowsBySource.get(key) ?? []
  }

  /** Every row currently loaded, across every source. */
  allLoadedRows(): WorkbenchRow[] {
    return this.loadedRows()
  }

  /**
   * Replace one source's rows and rebuild the matching index over everything currently loaded.
   * The index has to span sources: a match is a relationship between rows from different feeds.
   */
  setRowsForSource(key: SourceKey, rows: WorkbenchRow[]): void {
    this.rowsBySource.set(key, rows)
    this.matching.init(this.loadedRows())
    this.rowsSubject.next()
  }

  private loadedRows(): WorkbenchRow[] {
    const rows: WorkbenchRow[] = []
    this.rowsBySource.forEach(sourceRows => rows.push(...sourceRows))
    return rows
  }

  // ---- selection + related-row highlighting ---------------------------------

  registerGridApi(role: GridRole, api: GridApi): void {
    this.apis[role] = api
  }

  reportSelection(role: GridRole, rowIds: string[]): void {
    const current = this.selectionSubject.value
    const next = new Set(rowIds)
    if (setsEqual(current[role], next)) return
    this.selectionSubject.next({ ...current, [role]: next })
  }

  /** Union of the row ids selected across both grids — the scope of a Match action. */
  allSelectedIds(): string[] {
    const selection = this.selectionSubject.value
    return [...selection.comparison, ...selection.base]
  }

  /** The selected rows across both grids, resolved against the loaded row set. */
  allSelectedRows(): WorkbenchRow[] {
    const ids = new Set(this.allSelectedIds())
    return this.loadedRows().filter(row => ids.has(row.rowId))
  }

  /**
   * Drop the selection in both grids. Called after a successful action: the actioned rows are about
   * to re-render with new Status and counts, so keeping them selected would leave the menu offering
   * an action against a stale view of them.
   */
  clearSelection(): void {
    const current = this.selectionSubject.value
    if (!current.comparison.size && !current.base.size) return
    this.selectionSubject.next(emptySelection())
    this.apis.comparison?.deselectAll()
    this.apis.base?.deselectAll()
  }

  /** True when both grids have a selection, so a cross-source Match is possible. */
  bothGridsHaveSelection(): boolean {
    const selection = this.selectionSubject.value
    return selection.comparison.size > 0 && selection.base.size > 0
  }

  /**
   * Rows related to the OTHER grid's selection through a server match — used to tint them in this
   * grid. Server truth, not a local group id: the contract has no group id, so the relationship comes
   * from the read response's match id columns (see {@link MatchingService.relatedRowIdsFor}).
   *
   * @param role the grid asking (it gets the ids related to its sibling's selection)
   * @returns the row ids to tint
   */
  relatedRowIdsForOther(role: GridRole): Observable<Set<string>> {
    const other = this.otherRole(role)
    return this.selectionSubject.pipe(
      map(selection => this.matching.relatedRowIdsFor([...selection[other]])),
      distinctUntilChanged(setsEqual),
    )
  }

  // ---- cross-filter ---------------------------------------------------------

  crossFilterFor(role: GridRole): Observable<string[] | null> {
    return this.crossFilterSubject.pipe(
      map(cf => cf[role]),
      distinctUntilChanged(),
    )
  }

  /**
   * Cross-filter the OTHER grid down to the matched rows, auto-switching that grid's source to the
   * one that can show them (Custody/Trading -> Prometheus, etc.).
   */
  crossFilterToMatches(
    fromRole: GridRole,
    target: MatchTargetKey,
    rowIds: string[],
  ): void {
    if (!rowIds.length) return
    const required = gridKeyOf(target)
    const other = this.otherRole(fromRole)
    const current = this.sourceSubject.value
    if (current[other] !== required) {
      const next: PerRole<SourceKey> = { ...current, [other]: required }
      if (required === 'api' && next[fromRole] === 'api') {
        next[fromRole] = fromRole === 'comparison' ? 'prom' : 'cw'
      }
      this.sourceSubject.next(next)
    }
    this.selectionSubject.next(emptySelection())
    this.crossFilterSubject.next({
      comparison: null,
      base: null,
      [other]: rowIds,
    })
  }

  clearCrossFilter(role: GridRole): void {
    const current = this.crossFilterSubject.value
    if (current[role] == null) return
    this.crossFilterSubject.next({ ...current, [role]: null })
  }
}
